import { Image } from "expo-image"
import { removeBgImage } from "rn-remove-image-bg"
import { Pressable, Text, View } from "react-native"
import { useRef, useState, type DragEvent, type ChangeEvent } from "react"
import MaterialDesignIcons from "@react-native-vector-icons/material-design-icons"

import { ImagePreviewModal } from "./image-preview-modal"
import type { ProductImageViewProps } from "./product-image-view"
import { useMutation } from "@tanstack/react-query"
import { useUploadImage } from "@src/hooks/use-product-image-upload"
import { trpc } from "@src/utils"
import { ar } from "i18n/ar"
import { ENV } from "utils/env"

export function ProductImageView({ images, id }: ProductImageViewProps) {
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const utils = trpc.useUtils()
  const uploadImage = useUploadImage()

  const deleteImage = trpc.images.delete.useMutation({
    onSuccess: () => utils.products.get.invalidate({ id }),
    onError: () => window.alert(ar.failedToDeleteImage),
  })

  const removeBackgroundImage = useMutation({
    mutationFn: (uri: string) => removeBgImage(uri, { useCache: true }),
    onSuccess: (data) => {
      if (data) setSelectedImage(data)
    },
    onError: (error) => window.alert(error?.message || ar.somethingWentWrong),
  })

  const safeIndex = Math.min(selectedIndex, Math.max(0, images.length - 1))
  const heroImage = images[safeIndex]
  const heroUri = heroImage?.key ? `${ENV.R2_URL}/${heroImage.key}` : undefined

  function handleWebClick() {
    if (selectedImage) return
    fileInputRef.current?.click()
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(true)
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(false)
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(false)
    const file = e.dataTransfer.files[0]
    if (file) setSelectedImage(URL.createObjectURL(file))
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) setSelectedImage(URL.createObjectURL(file))
    e.target.value = ""
  }

  function handleDeleteImage(key: string) {
    if (!window.confirm(ar.deleteImageConfirmation)) return
    deleteImage.mutate({ keys: [key] })
    if (selectedIndex >= images.length - 1) setSelectedIndex(Math.max(0, images.length - 2))
  }

  function handleSave() {
    if (!selectedImage) return
    uploadImage.mutate(
      { entity: "product", entityId: id, uri: selectedImage },
      {
        onSuccess() {
          setSelectedImage(null)
          utils.products.get.invalidate({ id })
        },
      },
    )
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Hero Image */}
      <div
        className={`w-48 h-48 md:w-64 md:h-64 mx-auto justify-center items-center rounded ${
          isDragActive ? "border-2 border-dashed border-blue-400 bg-blue-50" : "border border-gray-200 bg-white"
        }`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />
        <Pressable onPress={images.length === 0 ? handleWebClick : undefined} className="w-full h-full justify-center items-center relative">
          {heroUri ? (
            <Image
              source={heroUri}
              className="w-48 h-48 md:w-64 md:h-64 mx-auto"
              contentFit="contain"
              placeholder={heroImage.thumbhash}
              transition={250}
              loading="lazy"
              cachePolicy="memory"
            />
          ) : (
            <View className="items-center">
              <MaterialDesignIcons name="camera" size={50} color="gray" />
              <Text className="text-sm text-gray-600 mt-2">Drag &amp; drop or click to upload</Text>
            </View>
          )}
        </Pressable>
      </div>

      {/* Thumbnail Strip */}
      <div className="flex flex-row gap-2 flex-wrap justify-center px-1">
        {images.map((img, i) => (
          <div key={img.key} className="relative">
            <Pressable onPress={() => setSelectedIndex(i)} className={`rounded border-2 ${i === safeIndex ? "border-blue-500" : "border-gray-200"}`}>
              <Image
                source={`${ENV.R2_URL}/${img.key}`}
                className="w-14 h-14"
                contentFit="cover"
                placeholder={img.thumbhash}
                transition={250}
                loading="lazy"
                cachePolicy="memory"
              />
            </Pressable>
            <Pressable onPress={() => handleDeleteImage(img.key)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full items-center justify-center">
              <MaterialDesignIcons name="close" size={12} color="white" />
            </Pressable>
          </div>
        ))}

        {/* Add button */}
        <Pressable onPress={handleWebClick} className="w-14 h-14 rounded border-2 border-dashed border-gray-300 items-center justify-center bg-gray-50 hover:bg-gray-100">
          <MaterialDesignIcons name="plus" size={24} color="gray" />
        </Pressable>
      </div>

      <ImagePreviewModal
        visible={!!selectedImage}
        imageUri={selectedImage}
        onClose={() => setSelectedImage(null)}
        onConfirm={handleSave}
        onRemoveBackground={() => selectedImage && removeBackgroundImage.mutate(selectedImage)}
        isProcessing={removeBackgroundImage.isPending}
        isUploading={uploadImage.isPending}
      />
    </div>
  )
}
